import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid } from "@/comn/components";
import {useForm, useFetch, useStore, useToast, useModal, useResource, useGrid, usePage, useTheme} from "@/comn/hooks";
import { Link } from "react-router-dom";
import {SF_REVW_SRCH, SG_REVW_LIST, URLS, APIS} from "@/tra/tancis/ptli/revw/cstmr/services/PtliRevwService";


/**
 * PTLI0707012Q Component
 * -----------------------
 * - Customer Review List page with search and grid display.
 * - Uses `SF_REVW_SRCH` form schema for searching reviews by process name or user ID.
 * - Uses `SG_REVW_LIST` grid schema to display results in a paginated table.
 * - Search results are fetched via API (`APIS.getCstmrRevwList`) and stored in local store for persistence.
 * - Review ID in the grid is a clickable link navigating to the review detail page (PTLI0707013Q).
 * - Supports reset and search actions, with validation warnings for invalid input.
 */

export const PTLI0707012Q = (props: any) => {

    const { condData, callback, cstmRoleList} = props;
    const { pgeUid, menuId } = usePage({
        pgeUid: "ptli0707012q",
        auth: ["auth_ptli0019"]
    });
    const modal    = useModal();
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid });
    const { theme } = useTheme();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const toast = useToast();

    // Load reusable resources (dropdown codes etc.)
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "COM0024" },
        ],
    });

    // Search form setup using schema
    const form = {
        cstmrRevwSrch: useForm({
            defaultSchema: SF_REVW_SRCH,
            defaultValues: { ...pgeStore?.form } || {}, // Restore last search if available
        }),
    };

    // Grid setup for review list
    const grid = {
        cstmrRevwList: useGrid({
            defaultSchema: SG_REVW_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    // API call for fetching review list
    const fetch = {
        getCstmrRevwList: useFetch({
            api: (page = grid.cstmrRevwList.page) => {
                return APIS.getCstmrRevwList(form.cstmrRevwSrch.getValues(), page, grid.cstmrRevwList.size);
            },

            enabled: comnUtils.isEmpty(form.cstmrRevwSrch.errors) && form.cstmrRevwSrch.isSubmitted,
            key: [grid.cstmrRevwList.page, grid.cstmrRevwList.size],
            onSuccess: () => {
                // Save form + pagination in store for persistence
                setStore(pgeUid, {
                    form: form.cstmrRevwSrch.getValues(),
                    page: grid.cstmrRevwList.page,
                    size: grid.cstmrRevwList.size,
                });
            },
        })
    }

    // Handlers for search actions
    const handler = {
        getCstmrRevwList: () => {
            form.cstmrRevwSrch.handleSubmit(
                () => {
                    grid.cstmrRevwList.setPage(0);
                    fetch.getCstmrRevwList.fetch(0); // Fetch first page
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Show error if invalid
                },
            )();
        },
        onSrchBtnClick: form.cstmrRevwSrch.handleSubmit(
            () => {
                grid.cstmrRevwList.setPage(0);
                fetch.getCstmrRevwList.fetch(0);
            },
            () => toast.showToast({ type: "warning", content: "msg.00002" }), // Invalid input warning
        ),
    };

    // Custom render for grid cell (makes review ID clickable link)
    const render = {
        cell: {
            cstmrRevwUid: (props: any) => {
                const { rowValues } = props;
                const url = comnUtils.isEmpty(menuId)
                    ? `${URLS.ptli0707013q}`
                    : `/ptli/revw/cstmr/ptli0707013q`;

                return (
                    <Link to={`${url}/${rowValues.cstmrRevwUid}`}>
                        {rowValues.cstmrRevwUid}
                    </Link>
                );
            },
        },
    };

    // Initial data load
    useEffect(() => {
        handler.getCstmrRevwList();
    }, []);

    return (
        <Page>
            {/* Search Form */}
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.cstmrRevwSrch.schema.revwPrcssNm}></Group.Control>
                                <Group.Control {...form.cstmrRevwSrch.schema.usrId}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                        <Layout direction="row">
                            <Layout.Left>
                                <Layout.Left>
                                    <Button
                                        role="reset"
                                        onClick={() => {
                                            form.cstmrRevwSrch.reset();
                                        }}
                                    ></Button>
                                </Layout.Left>
                            </Layout.Left>
                            <Layout.Right>
                                <Button role={"search"} onClick={handler.onSrchBtnClick} /> {/* Search button */}
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>

            {/* Results Grid */}
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid {...grid.cstmrRevwList.grid} data={fetch.getCstmrRevwList.data?.cstmrRevwList} render={render}/>
                    </Group.Section>
                </Group.Body>
            </Group>

        </Page>
    );
};
